import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api, type SessionSummary } from "../api";
import { SessionView } from "../SessionView";
import { Dialog, Empty, ErrorMessage, Loading } from "../components";
import { count, money, timestamp, tokens } from "../format";
import { useForgeEvent } from "../hooks";
import { adminKeyOptions, keyChoiceGroups, keyName } from "../logKeys";
import { NoActivity } from "../NoActivity";
import { personLabel, personOptions } from "../personFilter";
import { pick } from "../search";
import { SearchPicker } from "../SearchPicker";
import { type PanelTab, RequestPanel, useCanSeeContent } from "../RequestDetail";
import { RangeToggle } from "./Usage";

const ms = (n: number | null) => (n === null ? "–" : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`);
function span(a: string | null, b: string) {
  if (!a) return "–";
  const m = Math.round((Date.parse(b) - Date.parse(a)) / 60_000);
  return m < 1 ? "< 1 min" : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
}
/** Rows open on click or Enter/Space, so the table works from the keyboard too. */
const openOn = (fn: () => void) => ({
  className: "clickable", tabIndex: 0, onClick: fn,
  onKeyDown: (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } },
});

type Sheet = { kind: "session"; session: SessionSummary } | { kind: "request"; requestId: string } | null;

/** Request metadata only. The gateway stores no prompt or completion text in its spend log. */
export function LogsPage({ admin = false }: { admin?: boolean }) {
  const [view, setView] = useState<"sessions" | "requests">("sessions");
  const [days, setDays] = useState(7);
  const [model, setModel] = useState("");
  const [person, setPerson] = useState("");
  // The key filter lives in the URL, so My Keys can link to one key's requests (TAI-2570).
  const [params, setParams] = useSearchParams();
  const keyId = params.get("key") ?? "";
  const setKey = (k: string) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (k) next.set("key", k); else next.delete("key");
    return next;
  }, { replace: true });
  const [sheet, setSheet] = useState<Sheet>(null);
  // The request sheet widens while What was sent is open, as the session panel does.
  const [requestTab, setRequestTab] = useState<PanelTab>("details");
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const viewToggle = useRef<HTMLElement>(null);
  useForgeEvent(viewToggle, "forge-button-toggle-group-change", (e: CustomEvent) => setView(e.detail === "requests" ? "requests" : "sessions"));
  const modelSel = useRef<HTMLElement>(null);
  useForgeEvent(modelSel, "change", (e) => setModel(String((e.target as HTMLInputElement).value ?? "")));

  return (
    <>
      {!admin && (
        <p className="forge-typography--body2 muted">
          Every request made with your keys, grouped into the sessions your tools ran. Open one for timing, tokens,
          cache use and cost, and for what was sent: only you can see that. Choose a key to see only its requests,
          including a service key you own or back up; for a service key you see its requests, never what was sent.
        </p>
      )}
      <div className="filter-bar">
        <forge-button-toggle-group ref={viewToggle} value={view} mandatory dense aria-label="View">
          <forge-button-toggle value="sessions">Sessions</forge-button-toggle>
          <forge-button-toggle value="requests">Requests</forge-button-toggle>
        </forge-button-toggle-group>
        <RangeToggle value={days} onChange={setDays} options={[1, 7, 30]} />
        <forge-select label-position="inline-start" ref={modelSel} label="Model" value={model} dense style={{ minWidth: "200px" }}>
          <forge-option value="">Any</forge-option>
          {(models.data ?? []).map((m) => <forge-option key={m} value={m}>{m}</forge-option>)}
        </forge-select>
        {admin && <PersonFilter value={person} onChange={setPerson} />}
        {admin ? <AdminKeyFilter value={keyId} onChange={setKey} /> : <KeyFilter value={keyId} onChange={setKey} />}
      </div>

      {view === "sessions"
        ? <SessionsTable admin={admin} days={days} model={model} userId={person} keyId={keyId} onOpen={(s) => setSheet({ kind: "session", session: s })} />
        : <RequestsTable admin={admin} days={days} model={model} userId={person} keyId={keyId} onOpen={(id) => setSheet({ kind: "request", requestId: id })} />}

      <Dialog open={!!sheet} onClose={() => { setSheet(null); setRequestTab("details"); }} label={sheet?.kind === "session" ? "Session" : "Request details"} preset="right-sheet"
        wide={(sheet?.kind === "session" && !!sheet.session.sessionId) || (requestTab === "sent" && sheet?.kind !== "session")}>
        {sheet?.kind === "request" && <RequestSheet requestId={sheet.requestId} tab={requestTab} onTab={setRequestTab} />}
        {sheet?.kind === "session" && (sheet.session.sessionId
          ? <SessionView session={sheet.session} canReview={!!me.data?.sessionReview && sheet.session.userId === me.data.user.userId} />
          : <RequestSheet requestId={sheet.session.representativeRequestId} tab={requestTab} onTab={setRequestTab} />)}
        <div><forge-button onClick={() => { setSheet(null); setRequestTab("details"); }}>Close</forge-button></div>
      </Dialog>
    </>
  );
}

/**
 * Administration only: whose requests to show, "" for everyone (TAI-2305). Its own component, mounted
 * with the filter bar, so its listener binds (TAI-2302). Type an email or a user id to narrow the list.
 */
function PersonFilter({ value, onChange }: { value: string; onChange: (userId: string) => void }) {
  const users = useQuery({ queryKey: ["admin-users"], queryFn: api.adminUsers });
  const options = useMemo(() => personOptions(users.data ?? []), [users.data]);
  const filter = useMemo(() => (text: string, chosen: string | null) => pick(options, text, chosen), [options]);
  return <SearchPicker id="admin-person" label="Person" placeholder="Anyone" value={value} filter={filter} onChange={onChange} />;
}

/**
 * The Key choice (TAI-2570): this person's personal keys and the service keys they own or back up,
 * as the server lists them; "" for every key of theirs, which is the log as it was before. The select
 * mounts once the list has loaded, so it has its options when its value is set.
 */
function KeyFilter({ value, onChange }: { value: string; onChange: (keyId: string) => void }) {
  const choices = useQuery({ queryKey: ["log-keys"], queryFn: api.logKeys });
  if (!choices.data || (choices.data.length === 0 && !value)) return null;
  return <KeySelect groups={keyChoiceGroups(choices.data)} value={value} onChange={onChange} />;
}

/** Its own component, so its listener binds when the select exists, not while the list loads (TAI-2302). */
function KeySelect({ groups: { personal, service }, value, onChange }: {
  groups: ReturnType<typeof keyChoiceGroups>; value: string; onChange: (keyId: string) => void;
}) {
  const ref = useRef<HTMLElement>(null);
  useForgeEvent(ref, "change", (e) => onChange(String((e.target as HTMLInputElement).value ?? "")));
  return (
    <forge-select label-position="inline-start" ref={ref} label="Key" value={value} dense style={{ minWidth: "260px" }}>
      {/* Inside a group of its own: Forge drops an ungrouped option once a select has groups. */}
      <forge-option-group>
        <forge-option value="">All my keys</forge-option>
      </forge-option-group>
      {personal.length > 0 && (
        <forge-option-group label="Your keys">
          {personal.map((o) => <forge-option key={o.value} value={o.value}>{o.label}</forge-option>)}
        </forge-option-group>
      )}
      {service.length > 0 && (
        <forge-option-group label="Service keys you own or back up">
          {service.map((o) => <forge-option key={o.value} value={o.value}>{o.label}</forge-option>)}
        </forge-option-group>
      )}
    </forge-select>
  );
}

/** Administration only: any key, by name, owner or hash (TAI-2570). Built like the Person filter. */
function AdminKeyFilter({ value, onChange }: { value: string; onChange: (keyId: string) => void }) {
  const keys = useQuery({ queryKey: ["admin-keys"], queryFn: api.adminKeys });
  const options = useMemo(() => adminKeyOptions(keys.data ?? []), [keys.data]);
  const filter = useMemo(() => (text: string, chosen: string | null) => pick(options, text, chosen), [options]);
  return <SearchPicker id="admin-key" label="Key" placeholder="Any key" value={value} filter={filter} emptyMessage="No key matches"
    onChange={onChange} />;
}

/** Page and page size, back to page 1 whenever `filters` (the range, model, person, key and status) change. */
function usePaging(filters: string) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [seen, setSeen] = useState(filters);
  if (seen !== filters) {
    setSeen(filters);
    setPage(1);
  }
  return { page, pageSize, set: (pg: number, size: number) => { setPage(pg); setPageSize(size); } };
}

/**
 * Its own component so its listener attaches when the paginator exists: the tables render a
 * loading state first, and a listener bound then never fires (TAI-2302). `total` must be a
 * number: React sets it as a property, and Forge reads a string as 0, which disabled Next.
 */
function Paginator({ p, total }: { p: ReturnType<typeof usePaging>; total: number }) {
  const ref = useRef<HTMLElement>(null);
  useForgeEvent(ref, "forge-paginator-change", (e: CustomEvent<{ pageIndex: number; pageSize: number }>) =>
    p.set(e.detail.pageIndex + 1, e.detail.pageSize));
  return <forge-paginator ref={ref} total={total} pageSize={p.pageSize} pageIndex={p.page - 1} pageSizeOptions={[10, 25, 50, 100]} />;
}

function SessionsTable({ admin, days, model, userId, keyId, onOpen }: {
  admin: boolean; days: number; model: string; userId: string; keyId: string; onOpen: (s: SessionSummary) => void;
}) {
  const p = usePaging(`${days}|${model}|${userId}|${keyId}`);
  const q = useQuery({
    queryKey: ["sessions", admin, days, model, userId, keyId, p.page, p.pageSize],
    queryFn: () => api.sessions({ days, model, userId, keyId, page: p.page, pageSize: p.pageSize }, admin),
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <forge-card><Loading /></forge-card>;
  if (q.error) return <ErrorMessage error={q.error} />;
  if (q.data.sessions.length === 0) {
    return <forge-card>{admin || model || keyId ? <Empty title="No sessions match" message="Try a longer date range or clear the filter." />
      : <NoActivity quiet={{ title: "No sessions in this period", message: "Try a longer date range." }} />}</forge-card>;
  }
  return (
    <forge-card>
      <div className="table-wrap">
        <table className="forge-data-table">
          <thead>
            <tr>
              <th>Last activity</th>{admin && <th>Person</th>}<th>Tool</th><th className="num">Requests</th>
              <th className="num">Cost (est.)</th><th className="num">Tokens</th><th className="num">Length</th><th>Models</th><th>Key</th>
            </tr>
          </thead>
          <tbody>
            {q.data.sessions.map((s) => (
              <tr key={`${s.sessionId ?? s.representativeRequestId}|${s.keyId}`} {...openOn(() => onOpen(s))}
                aria-label={`Session with ${s.requests} requests, open details`}>
                <td>{timestamp(s.lastActivity)}</td>
                {admin && <td>{s.userId ? personLabel(s.userId) : ""}</td>}
                <td>{s.tool ?? <span className="muted">unknown</span>}</td>
                <td className="num">{count(s.requests)}</td>
                <td className="num">{money(s.spend)}</td>
                <td className="num">{tokens(s.totalTokens)}</td>
                <td className="num">{span(s.firstActivity, s.lastActivity)}</td>
                <td title={s.models.join(", ")}>{s.models.length > 2 ? `${s.models.slice(0, 2).join(", ")} +${s.models.length - 2}` : s.models.join(", ")}</td>
                <td className="mono">{keyName(s.keyAlias, s.keyId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator p={p} total={q.data.total} />
      <p className="muted">{count(q.data.total)} sessions. A session is one tool conversation on one key.</p>
    </forge-card>
  );
}

function RequestsTable({ admin, days, model, userId, keyId, onOpen }: {
  admin: boolean; days: number; model: string; userId: string; keyId: string; onOpen: (id: string) => void;
}) {
  const [status, setStatus] = useState("");
  const p = usePaging(`${days}|${model}|${userId}|${keyId}|${status}`);
  const statusSel = useRef<HTMLElement>(null);
  useForgeEvent(statusSel, "change", (e) => setStatus(String((e.target as HTMLInputElement).value ?? "")));
  const q = useQuery({
    queryKey: ["logs", admin, days, p.page, p.pageSize, status, model, userId, keyId],
    queryFn: () => api.logs({ days, page: p.page, pageSize: p.pageSize, status, model, userId, keyId }, admin),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <div className="filter-bar"><forge-select label-position="inline-start" ref={statusSel} label="Status" value={status} dense style={{ minWidth: "160px" }}>
        <forge-option value="">Any</forge-option>
        <forge-option value="success">Succeeded</forge-option>
        <forge-option value="failure">Failed</forge-option>
      </forge-select></div>
      <forge-card>
        {q.isPending ? <Loading /> : q.error ? <ErrorMessage error={q.error} /> : q.data.rows.length === 0 ? (
          (admin || status || model || keyId) ? <Empty title="No requests match" message="Try a longer date range or clear the filters." />
            : <NoActivity quiet={{ title: "No requests in this period", message: "Try a longer date range." }} />
        ) : (
          <>
            <div className="table-wrap">
              <table className="forge-data-table">
                <thead>
                  <tr>
                    <th>Time</th>{admin && <th>Person</th>}<th>Status</th><th>Model</th><th>Tool</th><th className="num">Cost (est.)</th>
                    <th className="num">Input</th><th className="num">Cache read</th><th className="num">Output</th>
                    <th className="num">Duration</th><th>Key</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.rows.map((r) => (
                    <tr key={r.requestId} {...openOn(() => onOpen(r.requestId))} aria-label={`Request at ${timestamp(r.startTime)}, open details`}>
                      <td>{timestamp(r.startTime)}</td>
                      {admin && <td>{r.userId ? personLabel(r.userId) : ""}</td>}
                      <td><span className={`status-dot ${r.status === "success" ? "status-dot--ok" : "status-dot--fail"}`} />{r.status === "success" ? "OK" : "Failed"}</td>
                      <td>{r.model}</td>
                      <td>{r.tool ?? <span className="muted">unknown</span>}</td>
                      <td className="num">{r.cacheHit ? "$0 (cache)" : money(r.spend)}</td>
                      <td className="num">{tokens(r.promptTokens)}</td>
                      <td className="num">{tokens(r.cacheReadTokens)}</td>
                      <td className="num">{tokens(r.completionTokens)}</td>
                      <td className="num">{ms(r.durationMs)}</td>
                      <td className="mono">{keyName(r.keyAlias, r.keyId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginator p={p} total={q.data.total} />
            <p className="muted">{count(q.data.total)} requests in the last {days === 1 ? "24 hours" : `${days} days`}.</p>
          </>
        )}
      </forge-card>
    </>
  );
}

function RequestSheet({ requestId, tab, onTab }: { requestId: string; tab: PanelTab; onTab: (t: PanelTab) => void }) {
  const q = useQuery({ queryKey: ["log", requestId], queryFn: () => api.logDetail(requestId) });
  // Someone else's request (an administrator's view) has no content tab, so it must not widen.
  const canSee = useCanSeeContent(q.data);
  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  return <RequestPanel d={q.data} tab={canSee ? tab : "details"} onTab={onTab} />;
}
